import { useAuth, useUser } from "@clerk/react"
import { ProductType } from "@repo/api-dtos"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { LoadingSpinner } from "@/components/global/elements/loading-spinner"
import { PageHeader } from "@/components/layout"
import { DeletedProductNotice } from "@/components/manage-content/deleted-product-notice"
import { ManageProductForm } from "@/components/manage-content/manage-product-form"
import { useOrpc } from "@/integrations/orpc"
import { useProductionEntity } from "@/queries/production/use-production-entity"
import {
	detectFieldDifferences,
	transformProductionToFormData,
	transformStagingToFormData,
} from "@/utils/form.helpers"

export const Route = createFileRoute("/products/singles/$id/manage")({
	component: ManageProductPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			gameSlug: search.gameSlug as string | undefined,
			expansionSlug: search.expansionSlug as string | undefined,
		}
	},
})

function ManageProductPage() {
	const { id } = Route.useParams()
	const searchParams = Route.useSearch()
	const { user } = useUser()
	const userEmail =
		user?.primaryEmailAddress?.emailAddress ??
		user?.emailAddresses?.[0]?.emailAddress ??
		"unknown@cardnexus.com"
	const { client } = useOrpc()
	const { isSignedIn } = useAuth()

	const gameSlug = searchParams.gameSlug || ""
	const productSlug = id
	const expansionSlug = searchParams.expansionSlug

	const {
		data: stagingData,
		isLoading: stagingLoading,
		error: stagingError,
	} = useQuery({
		queryKey: [
			"gameAdmin",
			"staging",
			"getEntity",
			"product",
			gameSlug,
			productSlug,
		],
		queryFn: async () => {
			const result = await client.staging.getEntity({
				entityType: "product",
				gameSlug: gameSlug,
				entitySlug: productSlug,
			})

			if (result.entityType !== "product") {
				throw new Error(`Expected product entity, got ${result.entityType}`)
			}

			if (result._staging?.isDeleted) {
				return result
			}

			if (
				(result.data as Record<string, unknown>)?.productType !==
				ProductType.CARD
			) {
				throw new Error(
					`Expected CARD product, got ${(result.data as Record<string, unknown>)?.productType}`,
				)
			}

			return result
		},
		enabled: isSignedIn && !!gameSlug && !!productSlug,
	})

	const { data: productionData, isLoading: productionLoading } =
		useProductionEntity({
			gameSlug,
			entitySlug: productSlug,
			entityType: "product",
			enabled: isSignedIn && !!gameSlug && !!productSlug,
		})

	const stagingFormData = transformStagingToFormData(stagingData)
	const productionFormData = transformProductionToFormData(productionData)

	const fieldDifferences = detectFieldDifferences(
		productionFormData,
		stagingFormData,
	)

	const isLoading = stagingLoading || productionLoading

	if (isLoading) {
		return (
			<div className="flex justify-center p-6">
				<LoadingSpinner />
			</div>
		)
	}

	if (stagingData?._staging?.isDeleted) {
		return (
			<DeletedProductNotice
				gameSlug={gameSlug}
				productSlug={productSlug}
				productName={(productionData as { name?: string } | undefined)?.name}
				markedAt={stagingData._staging.lastModified}
				isInProduction={Boolean(productionData)}
			/>
		)
	}

	if (stagingError || !stagingFormData) {
		return (
			<div className="p-6">
				<PageHeader
					title="Product not found"
					description={
						stagingError
							? stagingError.message
							: `The product with ID ${id} could not be found.`
					}
				/>
			</div>
		)
	}

	return (
		<ManageProductForm
			productionData={productionFormData}
			stagingData={stagingFormData}
			fieldDifferences={fieldDifferences}
			gameSlug={gameSlug}
			productSlug={productSlug}
			userEmail={userEmail}
			expansionSlug={expansionSlug}
			productType={ProductType.CARD}
		/>
	)
}
